/**
 * Starting workflows (job types). All share one backbone; each adds its own stages.
 * Edit them freely in Settings → Workflows.
 *
 * Stage kinds: "open" (still being worked), "closed" (finished and paid),
 * "lost" (didn't sell or was cancelled). Reports and the dashboard use the kind,
 * never a stage's name.
 */

export type StageKind = "open" | "closed" | "lost";

export const STAGE_KINDS: { value: StageKind; label: string }[] = [
  { value: "open", label: "Open" },
  { value: "closed", label: "Closed (done)" },
  { value: "lost", label: "Lost or cancelled" },
];

export interface WorkflowTemplate {
  name: string;
  /** Shows the insurance section (carrier, claim, deductible) on its jobs. */
  insurance: boolean;
  stages: { name: string; kind: StageKind }[];
  /** Stage whose arrival creates the QuickBooks sub-customer. */
  qbCreateStage: string;
  /** Where jobs move (forward only) when a proposal is sent, and when one is signed. */
  proposalSentStage?: string;
  proposalSignedStage?: string;
}

const open = (name: string) => ({ name, kind: "open" as const });

/** Lead → Inspection → Proposal → Sold → Scheduled → In Progress → Invoiced → Paid → Closed, plus each type's extras. */
function backbone(extras: { after: string; stages: string[] }[]): WorkflowTemplate["stages"] {
  const base = ["Lead", "Inspection", "Proposal", "Sold", "Scheduled", "In Progress", "Invoiced", "Paid"];
  const out: string[] = [];
  for (const s of base) {
    out.push(s);
    for (const e of extras) if (e.after === s) out.push(...e.stages);
  }
  return [...out.map(open), { name: "Closed", kind: "closed" }, { name: "Lost", kind: "lost" }];
}

export const DEFAULT_WORKFLOWS: WorkflowTemplate[] = [
  {
    name: "Insurance Claim",
    insurance: true,
    stages: backbone([
      { after: "Inspection", stages: ["Claim Filed", "Adjuster Meeting", "Scope Received"] },
      { after: "In Progress", stages: ["Supplements"] },
      { after: "Paid", stages: ["Depreciation Collected"] },
    ]),
    qbCreateStage: "Sold",
    proposalSentStage: "Proposal",
    proposalSignedStage: "Sold",
  },
  {
    name: "Retail Roof",
    insurance: false,
    stages: backbone([{ after: "Sold", stages: ["Deposit"] }]),
    qbCreateStage: "Sold",
    proposalSentStage: "Proposal",
    proposalSignedStage: "Sold",
  },
  {
    name: "Remodel",
    insurance: false,
    stages: backbone([
      { after: "Sold", stages: ["Design/Selections", "Permits"] },
      { after: "In Progress", stages: ["Draw Schedule"] },
    ]),
    qbCreateStage: "Sold",
    proposalSentStage: "Proposal",
    proposalSignedStage: "Sold",
  },
  {
    name: "Commercial",
    insurance: false,
    stages: backbone([{ after: "Proposal", stages: ["Bid Submitted", "Contract Review"] }]),
    qbCreateStage: "Sold",
    proposalSentStage: "Proposal",
    proposalSignedStage: "Sold",
  },
];
